import { Header, Sidebar } from '@/components/layout';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { UsageBreakdownCharts } from './UsageBreakdownCharts';
import { UsageClinicTable } from './UsageClinicTable';
import { UsageSummaryCards } from './UsageSummaryCards';
import { UsageTrendCard } from './UsageTrendCard';
import { UsageUserTable } from './UsageUserTable';
import { useUsageCostFilters } from './useUsageCostFilters';
import { useUsageCostQueries } from './useUsageCostQueries';

const ALL_FILTER_VALUE = '__all__';

const numberFilterValue = (value: number | null | undefined) => (value == null ? ALL_FILTER_VALUE : String(value));

const numberFromFilterValue = (value: string) => (value === ALL_FILTER_VALUE ? null : Number(value));

const textFilterValue = (value: string | null | undefined) => value || ALL_FILTER_VALUE;

const textFromFilterValue = (value: string) => (value === ALL_FILTER_VALUE ? null : value);

const userLabel = (user: { name: string | null; email: string | null; user_id: number }) =>
  user.name || user.email || `Notandi ${user.user_id}`;

const uniqueModelOptions = (models: { key: string; model: string }[]) => {
  const seen = new Set<string>();
  return models.filter((model) => {
    if (seen.has(model.model)) return false;
    seen.add(model.model);
    return true;
  });
};

export const UsageCostsPage = () => {
  const {
    state,
    filters,
    setRangePreset,
    setDate,
    setFilter,
    setTrendGroup,
    setTrendMetric,
    clearFilters,
    setClinicFilter,
    setUserFilter,
  } = useUsageCostFilters();

  const {
    summaryQuery,
    timelineQuery,
    featureBreakdownQuery,
    clinicsQuery,
    usersQuery,
    dataQualityQuery,
    trendsQuery,
    modelBreakdownQuery,
    metadataQuery,
  } = useUsageCostQueries(filters, state.trendGroup, state.trendMetric);

  const metadata = metadataQuery.data;
  const modelOptions = uniqueModelOptions(metadata?.models ?? []);
  const hasActiveFilters =
    state.clinicId != null || state.userId != null || Boolean(state.feature || state.provider || state.model);

  return (
    <div className="flex h-screen overflow-hidden bg-background">
      <Sidebar />
      <div className="flex min-w-0 flex-1 flex-col">
        <Header title="Notkun og kostnaður" />
        <main className="flex-1 overflow-y-auto p-8">
          <div className="mx-auto max-w-7xl space-y-8">
            <div className="flex flex-col gap-4 lg:flex-row lg:items-start lg:justify-between">
              <div>
                <h1 className="font-semibold text-3xl tracking-normal">Notkun og kostnaður</h1>
                <p className="mt-2 text-muted-foreground text-sm">
                  Yfirlit yfir heildarkostnað, atburði, hljóðmínútur og gagnagöt.
                </p>
              </div>
              <div className="flex flex-wrap items-center gap-2">
                <Select
                  value={state.rangePreset}
                  onValueChange={(value) => setRangePreset(value as typeof state.rangePreset)}
                >
                  <SelectTrigger className="w-[180px]" aria-label="Tímabil">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="7d">Síðustu 7 dagar</SelectItem>
                    <SelectItem value="30d">Síðustu 30 dagar</SelectItem>
                    <SelectItem value="current-month">Núverandi mánuður</SelectItem>
                    <SelectItem value="previous-month">Fyrri mánuður</SelectItem>
                    <SelectItem value="custom">Sérsniðið</SelectItem>
                  </SelectContent>
                </Select>
                {hasActiveFilters && (
                  <Button type="button" variant="outline" onClick={clearFilters}>
                    Hreinsa síur
                  </Button>
                )}
              </div>
            </div>

            <div className="grid grid-cols-1 gap-3 rounded-md border p-4 md:grid-cols-2 xl:grid-cols-7">
              <div className="space-y-1.5">
                <Label htmlFor="usage-start-date">Frá</Label>
                <Input
                  id="usage-start-date"
                  type="date"
                  value={state.startDate}
                  onChange={(event) => setDate({ startDate: event.target.value })}
                />
              </div>
              <div className="space-y-1.5">
                <Label htmlFor="usage-end-date">Til</Label>
                <Input
                  id="usage-end-date"
                  type="date"
                  value={state.endDate}
                  onChange={(event) => setDate({ endDate: event.target.value })}
                />
              </div>
              <div className="space-y-1.5">
                <Label htmlFor="usage-clinic">Stofa</Label>
                <Select
                  value={numberFilterValue(state.clinicId)}
                  onValueChange={(value) => setFilter({ clinicId: numberFromFilterValue(value), userId: null })}
                  disabled={metadataQuery.isLoading}
                >
                  <SelectTrigger id="usage-clinic" aria-label="Stofa">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value={ALL_FILTER_VALUE}>Allar stofur</SelectItem>
                    {(metadata?.clinics ?? []).map((clinic) => (
                      <SelectItem key={clinic.clinic_id} value={String(clinic.clinic_id)}>
                        {clinic.clinic_name}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-1.5">
                <Label htmlFor="usage-user">Notandi</Label>
                <Select
                  value={numberFilterValue(state.userId)}
                  onValueChange={(value) => setFilter({ userId: numberFromFilterValue(value) })}
                  disabled={metadataQuery.isLoading}
                >
                  <SelectTrigger id="usage-user" aria-label="Notandi">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value={ALL_FILTER_VALUE}>Allir notendur</SelectItem>
                    {(metadata?.users ?? []).map((user) => (
                      <SelectItem key={user.user_id} value={String(user.user_id)}>
                        {userLabel(user)}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-1.5">
                <Label htmlFor="usage-feature">Tegund</Label>
                <Select
                  value={textFilterValue(state.feature)}
                  onValueChange={(value) => setFilter({ feature: textFromFilterValue(value) })}
                  disabled={metadataQuery.isLoading}
                >
                  <SelectTrigger id="usage-feature" aria-label="Tegund">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value={ALL_FILTER_VALUE}>Allar tegundir</SelectItem>
                    {(metadata?.features ?? []).map((feature) => (
                      <SelectItem key={feature} value={feature}>
                        {feature}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-1.5">
                <Label htmlFor="usage-provider">Veitandi</Label>
                <Select
                  value={textFilterValue(state.provider)}
                  onValueChange={(value) => setFilter({ provider: textFromFilterValue(value) })}
                  disabled={metadataQuery.isLoading}
                >
                  <SelectTrigger id="usage-provider" aria-label="Veitandi">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value={ALL_FILTER_VALUE}>Allir veitendur</SelectItem>
                    {(metadata?.providers ?? []).map((provider) => (
                      <SelectItem key={provider} value={provider}>
                        {provider}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-1.5">
                <Label htmlFor="usage-model">Líkan</Label>
                <Select
                  value={textFilterValue(state.model)}
                  onValueChange={(value) => setFilter({ model: textFromFilterValue(value) })}
                  disabled={metadataQuery.isLoading}
                >
                  <SelectTrigger id="usage-model" aria-label="Líkan">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value={ALL_FILTER_VALUE}>Öll líkön</SelectItem>
                    {modelOptions.map((model) => (
                      <SelectItem key={model.key} value={model.model}>
                        {model.model}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>

            <UsageSummaryCards
              summary={summaryQuery.data}
              quality={dataQualityQuery.data}
              isLoading={summaryQuery.isLoading}
              isError={summaryQuery.isError}
              error={summaryQuery.error}
              onRetry={() => void summaryQuery.refetch()}
              hasActiveFilters={hasActiveFilters}
              isQualityLoading={dataQualityQuery.isLoading}
              isQualityError={dataQualityQuery.isError}
              qualityError={dataQualityQuery.error}
              onQualityRetry={() => void dataQualityQuery.refetch()}
            />

            <UsageTrendCard
              timeline={timelineQuery.data ?? []}
              series={state.trendGroup === 'overall' ? [] : (trendsQuery.data?.series ?? [])}
              view={state.trendGroup}
              metric={state.trendMetric}
              isLoading={timelineQuery.isLoading || trendsQuery.isLoading}
              isError={timelineQuery.isError || trendsQuery.isError}
              error={timelineQuery.error ?? trendsQuery.error}
              onRetry={() => {
                void timelineQuery.refetch();
                void trendsQuery.refetch();
              }}
              onViewChange={setTrendGroup}
              onMetricChange={setTrendMetric}
              onClinicFilter={setClinicFilter}
              onUserFilter={setUserFilter}
            />

            <UsageBreakdownCharts
              featureRows={featureBreakdownQuery.data ?? []}
              modelRows={modelBreakdownQuery.data ?? []}
              isFeatureLoading={featureBreakdownQuery.isLoading}
              isModelLoading={modelBreakdownQuery.isLoading}
              isFeatureError={featureBreakdownQuery.isError}
              isModelError={modelBreakdownQuery.isError}
              featureError={featureBreakdownQuery.error}
              modelError={modelBreakdownQuery.error}
              onFeatureRetry={() => void featureBreakdownQuery.refetch()}
              onModelRetry={() => void modelBreakdownQuery.refetch()}
            />

            <div className="grid grid-cols-1 gap-6 xl:grid-cols-2">
              <UsageClinicTable
                clinics={clinicsQuery.data ?? []}
                isLoading={clinicsQuery.isLoading}
                isError={clinicsQuery.isError}
                error={clinicsQuery.error}
                onRetry={() => void clinicsQuery.refetch()}
                onClinicFilter={setClinicFilter}
              />
              <UsageUserTable
                users={usersQuery.data ?? []}
                isLoading={usersQuery.isLoading}
                isError={usersQuery.isError}
                error={usersQuery.error}
                onRetry={() => void usersQuery.refetch()}
                onUserFilter={setUserFilter}
              />
            </div>
          </div>
        </main>
      </div>
    </div>
  );
};
